import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, PlayCircle, X } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PROJECTS } from "../lib/constants";
import { fileUrl, listProjects } from "../lib/api";

const CATEGORY_FILTERS = ["All", "Residential", "Commercial", "Interiors", "PEB", "Structural", "Drawings"];
const STATUS_FILTERS = [
  { key: "all", label: "All Work" },
  { key: "completed", label: "Completed" },
  { key: "ongoing", label: "Ongoing" },
  { key: "upcoming", label: "Upcoming" },
];
const STATUS_META = {
  completed: { label: "Completed", color: "var(--m-success)" },
  ongoing: { label: "Ongoing", color: "var(--m-warning)" },
  upcoming: { label: "Upcoming", color: "var(--m-teal)" },
};

export default function Projects() {
  const [cat, setCat] = useState("All");
  const [statusFilter, setStatusFilter] = useState("all");
  const [uploaded, setUploaded] = useState([]);
  const [active, setActive] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listProjects()
      .then((data) => { if (!cancelled) setUploaded(data || []); })
      .catch((err) => { console.error("Failed to load projects:", err); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const merged = useMemo(() => [
    ...uploaded.map((p) => ({
      title: p.title,
      category: p.category,
      location: p.location,
      image: p.image_url ? fileUrl(p.image_url) : "",
      video: p.video_url ? fileUrl(p.video_url) : "",
      spec: p.spec,
      status: p.status || "completed",
      year: p.year || "",
      _real: true,
      _id: p.id,
    })),
    ...PROJECTS.map((p) => ({ ...p, status: "completed", _real: false })),
  ], [uploaded]);

  const visible = merged.filter((p) => {
    if (cat !== "All" && p.category !== cat) return false;
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    return true;
  });

  const counts = useMemo(() => ({
    all: merged.length,
    completed: merged.filter((p) => p.status === "completed").length,
    ongoing: merged.filter((p) => p.status === "ongoing").length,
    upcoming: merged.filter((p) => p.status === "upcoming").length,
  }), [merged]);

  return (
    <main data-testid="projects-page">
      <section className="bg-[var(--m-bg-2)] section-pad pt-32 md:pt-44">
        <div className="container-x">
          <span className="eyebrow">Selected Work</span>
          <div className="gold-divider mt-4" />
          <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[1] tracking-tight max-w-4xl">
            Drawings turned into <em className="italic text-[var(--m-teal)]">landmarks</em>.
          </h1>
          <p className="mt-8 text-[var(--m-ink-2)] text-lg max-w-2xl leading-relaxed">
            A growing portfolio across residential towers, commercial complexes, interiors, PEB
            structures and approval-grade drawings — completed builds and works in progress.
          </p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x">
          {/* Status filter — primary */}
          <div className="flex flex-wrap gap-2 mb-6" data-testid="project-status-filters">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s.key}
                onClick={() => setStatusFilter(s.key)}
                className="px-5 py-2.5 text-xs tracking-[0.18em] uppercase transition-colors border font-medium"
                style={{
                  background: statusFilter === s.key ? "var(--m-teal)" : "transparent",
                  color: statusFilter === s.key ? "var(--m-bg)" : "var(--m-ink)",
                  borderColor: statusFilter === s.key ? "var(--m-teal)" : "var(--m-border)",
                }}
                data-testid={`project-status-filter-${s.key}`}
              >
                {s.label} <span className="ml-1.5 opacity-70">({counts[s.key]})</span>
              </button>
            ))}
          </div>

          {/* Category filter — secondary */}
          <div className="flex flex-wrap gap-3 mb-12 pt-3 border-t border-[var(--m-border)]" data-testid="project-filters">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--m-ink-3)] mr-2 self-center">Category</span>
            {CATEGORY_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setCat(f)}
                className="px-4 py-1.5 text-xs tracking-[0.16em] uppercase transition-colors border"
                style={{
                  background: cat === f ? "var(--m-ink)" : "transparent",
                  color: cat === f ? "var(--m-bg)" : "var(--m-ink)",
                  borderColor: cat === f ? "var(--m-ink)" : "var(--m-border)",
                }}
                data-testid={`project-filter-${f.toLowerCase()}`}
              >
                {f}
              </button>
            ))}
          </div>

          {loaded && uploaded.length === 0 && (
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--m-ink-3)] mb-6">
              Showcase samples — real project gallery coming soon
            </div>
          )}

          {visible.length === 0 ? (
            <div className="bg-[var(--m-bg-2)] p-20 text-center text-[var(--m-ink-3)] border border-[var(--m-border)]">
              No projects match this filter. Try a different combination.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visible.map((p, i) => {
                const meta = STATUS_META[p.status] || STATUS_META.completed;
                return (
                  <Reveal key={(p._id || p.title) + i} delay={i * 50}>
                    <article
                      className="group cursor-pointer"
                      onClick={() => p.video && setActive(p)}
                      data-testid={`project-card-${i}`}
                    >
                      <div className="relative overflow-hidden bg-[var(--m-bg-3)]">
                        {p.video && !p.image ? (
                          <video src={p.video} className="w-full h-80 object-cover" muted />
                        ) : (
                          <img
                            src={p.image}
                            alt={p.title}
                            className="w-full h-80 object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                        )}
                        <span className="absolute top-4 left-4 tag tag-gold">{p.category}</span>
                        <span
                          className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.16em] uppercase font-semibold border bg-white/95 backdrop-blur"
                          style={{ color: meta.color, borderColor: meta.color }}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${p.status === "ongoing" ? "animate-pulse" : ""}`} style={{ background: meta.color }} />
                          {meta.label}
                        </span>
                        {p.video && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                            <PlayCircle size={56} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.2} />
                          </div>
                        )}
                      </div>
                      <div className="mt-5">
                        <h3 className="font-display text-2xl leading-tight">{p.title}</h3>
                        {(p.location || p.year) && (
                          <div className="mt-2 flex items-center gap-3 text-xs uppercase tracking-[0.16em] text-[var(--m-ink-3)] flex-wrap">
                            {p.location && <span className="flex items-center gap-1"><MapPin size={12} /> {p.location}</span>}
                            {p.year && <span>• {p.year}</span>}
                          </div>
                        )}
                        {p.spec && <p className="mt-3 text-sm text-[var(--m-ink-2)]">{p.spec}</p>}
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          )}

          <div className="mt-20 text-center">
            <p className="text-[var(--m-ink-2)] mb-6">
              {uploaded.length > 0 ? "More projects added regularly." : "More real photos & walkthroughs coming soon."}
            </p>
            <Link to="/quotation" className="btn-primary">
              Start Your Project <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {active && active.video && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="video-modal">
          <button className="absolute top-6 right-6 text-white p-2" onClick={() => setActive(null)}>
            <X size={26} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <video src={active.video} controls autoPlay className="w-full max-h-[80vh] object-contain bg-black" />
            <div className="text-white mt-4">
              <h3 className="font-display text-2xl">{active.title}</h3>
              {active.location && <p className="text-white/60 text-sm mt-1">{active.location}</p>}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
